import React, { useEffect, useState } from "react";
import { X, AlertTriangle, CheckCircle2, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatTL, extractErrorMessage, AYLAR } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function VarlikAuditModal({ onClose, onFixed }) {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prim/audit/varlik-mismatch");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Yüklenemedi"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFixAll = async () => {
    if (!window.confirm(`${rows.length} prim kaydı "Varlık Şirketi = Evet" olarak işaretlenecek ve oranları yeniden hesaplanacak. Devam?`)) return;
    setFixing(true);
    try {
      const r = await api.post("/prim/audit/varlik-mismatch/fix", {});
      toast.success(`${r.data?.updated || 0} prim düzeltildi`);
      await load();
      onFixed && onFixed();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Düzeltilemedi"));
    } finally {
      setFixing(false);
    }
  };

  const handleFixRow = async (primId) => {
    if (!window.confirm('Bu prim satırı "Varlık Şirketi = Evet" olarak işaretlenecek ve oranı yeniden hesaplanacak. Devam?')) return;
    setFixing(true);
    try {
      const r = await api.post("/prim/audit/varlik-mismatch/fix", { prim_ids: [primId] });
      toast.success(`${r.data?.updated || 0} prim düzeltildi`);
      await load();
      onFixed && onFixed();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Düzeltilemedi"));
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="varlik-audit-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 grid place-items-center">
              <AlertTriangle size={18} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Varlık Şirketi · Uyumsuzluk Denetimi</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Müvekkil adı "varlık yönetim" içeren ama <strong>Varlık Şirketi = Hayır</strong> olarak kaydedilmiş primler.
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="varlik-audit-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div
              data-testid="varlik-audit-count"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold border ${
                rows.length > 0
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700"
              }`}
            >
              {rows.length > 0 ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
              {rows.length === 0 ? "Uyumsuz kayıt yok" : `${rows.length} uyumsuz kayıt`}
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="varlik-audit-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Yenile
            </button>
          </div>
          {isAdmin && rows.length > 0 && (
            <button
              onClick={handleFixAll}
              disabled={fixing}
              data-testid="varlik-audit-fix-all"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold disabled:opacity-50"
            >
              <Wand2 size={14} /> {fixing ? "Düzeltiliyor..." : "Hepsini Düzelt"}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading && <div className="py-10 text-center text-slate-400 text-sm">Yükleniyor...</div>}
          {!loading && rows.length === 0 && (
            <div className="py-12 text-center">
              <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
              <div className="text-sm font-semibold text-slate-700">Harika — tüm "Varlık Yönetim" müvekkilleri doğru işaretlenmiş.</div>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs" data-testid="varlik-audit-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-bold">Tarih</th>
                    <th className="px-3 py-2 font-bold">Müvekkil</th>
                    <th className="px-3 py-2 font-bold">Dosya</th>
                    <th className="px-3 py-2 font-bold">İcra Föy / Esas</th>
                    <th className="px-3 py-2 font-bold text-right">Vekalet (₺)</th>
                    <th className="px-3 py-2 font-bold text-right">Tahsilat (₺)</th>
                    <th className="px-3 py-2 font-bold text-right">Oran (%)</th>
                    <th className="px-3 py-2 font-bold">Durum</th>
                    {isAdmin && <th className="px-3 py-2 font-bold text-right">İşlem</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const ay = AYLAR.find((x) => x.v === r.ay)?.n;
                    return (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-amber-50/40">
                        <td className="px-3 py-2 font-mono-tab text-slate-600">{r.tarih || `${r.yil}-${ay || r.ay}`}</td>
                        <td className="px-3 py-2 font-semibold text-slate-900">{r.alacakli_adi || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">{r.dosya_adi || "—"}</td>
                        <td className="px-3 py-2 font-mono-tab text-slate-600">{[r.icra_foy, r.icra_esas].filter(Boolean).join(" / ") || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono-tab text-slate-700">{formatTL(r.vekalet_ucreti)}</td>
                        <td className="px-3 py-2 text-right font-mono-tab text-slate-700">{formatTL(r.tahsilat_miktari)}</td>
                        <td className="px-3 py-2 text-right font-mono-tab text-slate-700">{r.oran != null ? `%${r.oran}` : "—"}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                            Varlık = Hayır
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => handleFixRow(r.id)}
                              disabled={fixing}
                              data-testid={`varlik-row-fix-${r.id}`}
                              title='Sadece bu satırı "Varlık Şirketi = Evet" olarak düzelt'
                              className="w-7 h-7 rounded text-indigo-600 hover:bg-indigo-50 inline-flex items-center justify-center disabled:opacity-50"
                            >
                              <Wand2 size={13} />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!isAdmin && (
                <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
                  Toplu düzeltme yalnızca yönetici tarafından yapılabilir.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
